import { DateTime } from 'luxon';
import {
  Bdm1Outcome,
  NachversandKind,
  RetrievalMode,
  RetrievalStatus,
} from '@prisma/client';
import {
  bdm1SwitchBlockReason,
  bdmDeadlinePassed,
  samplePickupDeadline,
  type Bdm1SwitchBlockReason,
  type ParticipantBdmStateResDto,
  type RetrievalResDto,
} from '@web/common';

export const STATUS: Record<
  RetrievalStatus,
  { labelKey: string; color: string }
> = {
  [RetrievalStatus.AWAITING_BOOKING]: {
    labelKey: 'Panel.Status.AwaitingBooking',
    color: 'gray',
  },
  [RetrievalStatus.BOOKED]: {
    labelKey: 'Panel.Status.Booked',
    color: 'warningAmber',
  },
  [RetrievalStatus.DISPATCHED]: {
    labelKey: 'Panel.Status.Dispatched',
    color: 'primary',
  },
  [RetrievalStatus.READY_FOR_PICKUP]: {
    labelKey: 'Panel.Status.Ready',
    color: 'warningAmber',
  },
  [RetrievalStatus.RETURNED_READY_FOR_PICKUP]: {
    labelKey: 'Panel.Status.Ready',
    color: 'warningAmber',
  },
  [RetrievalStatus.PICKED_UP]: {
    labelKey: 'Panel.Status.PickedUp',
    color: 'primary',
  },
  [RetrievalStatus.NO_FULFILLMENT]: {
    labelKey: 'Panel.Status.Voucher',
    color: 'gray',
  },
};

export const MODE_KEY: Record<RetrievalMode, string> = {
  [RetrievalMode.MAIL]: 'Panel.Mode.Mail',
  [RetrievalMode.PICKUP]: 'Panel.Mode.Pickup',
  [RetrievalMode.NONE]: 'Panel.Mode.None',
};

export const OUTCOME_KEY: Record<Bdm1Outcome, string> = {
  [Bdm1Outcome.VOUCHER]: 'Panel.Outcome.Voucher',
  [Bdm1Outcome.SAMPLE_LOCAL]: 'Panel.Outcome.SampleLocal',
  [Bdm1Outcome.SAMPLE_MAIL]: 'Panel.Outcome.SampleMail',
};

export type ActionKind = 'dispatch' | 'return' | 'pickup';

// Spiegelt canTransitionRetrievalStatus (SCC-732): welche Staff-Aktion ein Status erlaubt.
export const actionFor = (s: RetrievalStatus): ActionKind | null => {
  if (s === RetrievalStatus.BOOKED) {
    return 'dispatch';
  }
  if (s === RetrievalStatus.DISPATCHED) {
    return 'return';
  }
  if (
    s === RetrievalStatus.READY_FOR_PICKUP ||
    s === RetrievalStatus.RETURNED_READY_FOR_PICKUP
  ) {
    return 'pickup';
  }
  return null;
};

export const ACTIONS: Record<
  ActionKind,
  { labelKey: string; modalKey: string; nextStatus: RetrievalStatus }
> = {
  dispatch: {
    labelKey: 'Panel.Actions.Dispatch',
    modalKey: 'Dispatch',
    nextStatus: RetrievalStatus.DISPATCHED,
  },
  return: {
    labelKey: 'Panel.Actions.Return',
    modalKey: 'Return',
    nextStatus: RetrievalStatus.RETURNED_READY_FOR_PICKUP,
  },
  pickup: {
    labelKey: 'Panel.Actions.Pickup',
    modalKey: 'Pickup',
    nextStatus: RetrievalStatus.PICKED_UP,
  },
};

export type Pending = { kind: ActionKind; id: number };

export const fmtDate = (d: Date) =>
  DateTime.fromJSDate(d).toFormat('dd.MM.yyyy');

export type Bdm1SwitchEligibility = {
  blockReason: Bdm1SwitchBlockReason | null;
  // Informational only, never blocking. Staff can still mark the same retrieval as picked up
  // after day 27 too, so the switch stays available then as well. This tells staff by how many
  // days the deadline has already passed.
  daysOverdue: number | null;
};

// Layers the day-27 deadline (a display concern) on top of the shared hard block reason.
export const bdm1SwitchEligibility = (
  retrieval: RetrievalResDto | undefined,
  bdmState: ParticipantBdmStateResDto | null,
): Bdm1SwitchEligibility => {
  const blockReason = bdm1SwitchBlockReason(retrieval, bdmState?.bdm1Outcome);
  if (blockReason !== null || !bdmState?.bdmStartDate) {
    return { blockReason, daysOverdue: null };
  }

  const deadline = samplePickupDeadline(
    NachversandKind.BDM1,
    bdmState.bdmStartDate,
  );
  if (deadline !== null && bdmDeadlinePassed(deadline)) {
    const daysOverdue = Math.max(
      1,
      Math.floor(DateTime.now().diff(deadline, 'days').days),
    );
    return { blockReason: null, daysOverdue };
  }
  return { blockReason: null, daysOverdue: null };
};
